import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '온라인 상담신청 | 대구드레스룸 키친스',
    description: '대구드레스룸 키친스 온라인 상담 신청. 드레스룸, 붙박이장, 팬트리 시공 문의를 남겨주세요.',
    url: `${SITE_URL}/contact`,
    inLanguage: 'ko',
    isPartOf: {
      '@type': 'WebSite',
      name: '대구드레스룸 키친스',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: '대구드레스룸 키친스',
    url: SITE_URL,
    telephone: '053-766-0411',
    faxNumber: '053-766-0412',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '침산남로48',
      addressLocality: '북구',
      addressRegion: '대구광역시',
      addressCountry: 'KR',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  },
];

const constructionTypes = [
  '드레스룸 (화이트)',
  '드레스룸 (블랙)',
  '드레스룸 (우드)',
  '거실장',
  '붙박이장',
  '신발장',
  '팬트리',
  '다용도 수납장',
  '기타',
];

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setSubmitStatus('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d7fdih6ivmjfhtdrfu4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead
        title="온라인 상담신청 | 대구드레스룸 키친스"
        description="대구드레스룸 키친스 온라인 상담 신청. 드레스룸, 붙박이장, 팬트리, 거실장, 신발장 시공 문의를 남겨주세요. 053-766-0411"
        keywords="드레스룸상담, 붙박이장견적, 팬트리시공문의, 대구드레스룸"
        canonical={`${SITE_URL}/contact`}
        jsonLd={contactJsonLd}
      />
      <Navbar />

      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="w-full bg-yellow-400 py-14 px-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-semibold tracking-widest text-yellow-900/70 uppercase mb-2">Contact Us</p>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">온라인 상담 신청</h1>
            <p className="text-sm text-gray-800/70">
              궁금하신 사항을 남겨주시면 빠르게 연락드리겠습니다.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* 폼 */}
          <div className="lg:col-span-2">
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-20 gap-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-yellow-100">
                  <i className="ri-check-line text-yellow-500 text-3xl" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">상담 신청이 완료되었습니다!</h2>
                <p className="text-sm text-gray-500 text-center">
                  빠른 시일 내에 연락드리겠습니다.<br />감사합니다.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="mt-2 px-6 py-2.5 bg-yellow-400 text-gray-900 text-sm font-semibold rounded-lg hover:bg-yellow-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  다시 신청하기
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                {/* 이름 */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-600">
                    이름 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="성함을 입력해주세요"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                {/* 연락처 */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-600">
                    연락처 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="010-0000-0000"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                {/* 이메일 */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-600">이메일</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="example@email.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors"
                  />
                </div>

                {/* 시공 종류 */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-600">
                    시공 종류 <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="construction_type"
                    required
                    defaultValue=""
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-yellow-400 transition-colors cursor-pointer bg-white"
                  >
                    <option value="" disabled>시공 종류를 선택해주세요</option>
                    {constructionTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* 문의 내용 */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-600">
                    문의 내용 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="시공 공간, 평수, 원하시는 스타일 등 자세히 적어주시면 더 정확한 상담이 가능합니다."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors resize-none"
                  />
                  <p className={`text-xs text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                    {charCount} / 500
                  </p>
                </div>

                {submitStatus === 'error' && (
                  <p className="text-xs text-red-500 bg-red-50 rounded-lg px-4 py-3">
                    전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'loading' || charCount > 500}
                  className="w-full py-3.5 bg-yellow-400 text-gray-900 text-sm font-bold rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {submitStatus === 'loading' ? '전송 중...' : '상담 신청하기'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  <span className="text-red-400">*</span> 표시는 필수 입력 항목입니다.
                </p>
              </form>
            )}
          </div>

          {/* 사이드 정보 */}
          <div className="flex flex-col gap-5">
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-100">
              <h3 className="text-sm font-bold text-gray-800 mb-4">연락처 안내</h3>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 flex-shrink-0">
                    <i className="ri-phone-line text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">전화 상담</p>
                    <a href="tel:053-766-0411" className="text-sm font-bold text-gray-900 hover:text-yellow-600 transition-colors">
                      053-766-0411
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 flex-shrink-0">
                    <i className="ri-printer-line text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">팩스</p>
                    <p className="text-sm font-bold text-gray-900">053-766-0412</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">주소</p>
                    <p className="text-sm font-bold text-gray-900">대구광역시 북구 침산남로48</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 mb-3">상담 운영 시간</h3>
              <div className="flex flex-col gap-2 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>평일</span>
                  <span className="font-semibold text-gray-800">09:00 ~ 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>토요일</span>
                  <span className="text-gray-400">휴무</span>
                </div>
                <div className="flex justify-between">
                  <span>일요일 / 공휴일</span>
                  <span className="text-gray-400">휴무</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                온라인 상담은 24시간 접수 가능하며,<br />
                운영 시간 내 순차적으로 답변드립니다.
              </p>
            </div>

            <div className="bg-gray-900 rounded-xl p-6">
              <h3 className="text-sm font-bold text-white mb-2">빠른 전화 상담</h3>
              <p className="text-xs text-gray-400 mb-4">지금 바로 전화하시면 즉시 상담 가능합니다.</p>
              <a
                href="tel:053-766-0411"
                className="flex items-center justify-center gap-2 w-full py-3 bg-yellow-400 text-gray-900 text-sm font-bold rounded-lg hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-base" />
                053-766-0411
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
